import { type HTMLAttributes } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

export type LiveIndicatorSize = 'sm' | 'md' | 'lg';
export type LiveIndicatorVariant = 'success' | 'warning' | 'danger' | 'primary';

interface LiveIndicatorProps extends HTMLAttributes<HTMLDivElement> {
  size?: LiveIndicatorSize;
  variant?: LiveIndicatorVariant;
  showText?: boolean;
  text?: string;
}

const sizeStyles: Record<LiveIndicatorSize, { dot: string; text: string }> = {
  sm: { dot: 'h-1.5 w-1.5', text: 'text-xs' },
  md: { dot: 'h-2 w-2', text: 'text-sm' },
  lg: { dot: 'h-2.5 w-2.5', text: 'text-base' },
};

const variantStyles: Record<LiveIndicatorVariant, { dot: string; text: string; glow: string }> = {
  success: {
    dot: 'bg-emerald-500',
    text: 'text-emerald-500',
    glow: 'shadow-emerald-500/50',
  },
  warning: {
    dot: 'bg-amber-500',
    text: 'text-amber-500',
    glow: 'shadow-amber-500/50',
  },
  danger: {
    dot: 'bg-red-500',
    text: 'text-red-500',
    glow: 'shadow-red-500/50',
  },
  primary: {
    dot: 'bg-primary',
    text: 'text-primary',
    glow: 'shadow-primary/50',
  },
};

export const LiveIndicator = ({
  size = 'md',
  variant = 'success',
  showText = true,
  text = 'LIVE',
  className,
  ...props
}: LiveIndicatorProps) => {
  const { dot, text: textSize } = sizeStyles[size];
  const { dot: dotColor, text: textColor, glow } = variantStyles[variant];

  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 px-2.5 py-1 rounded-full',
        'bg-white border border-gray-200 shadow-sm',
        className
      )}
      {...props}
    >
      <span className="relative flex">
        <motion.span
          className={cn('absolute inline-flex rounded-full opacity-75', dot, dotColor)}
          animate={{ scale: [1, 1.5, 1], opacity: [0.75, 0, 0.75] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        />
        <span className={cn('relative inline-flex rounded-full shadow-lg', dot, dotColor, glow)} />
      </span>
      {showText && (
        <span className={cn('font-semibold tracking-wider', textSize, textColor)}>
          {text}
        </span>
      )}
    </div>
  );
};

// Service Status Indicator - for DDARP, WireGuard, BIRD
export type ServiceType = 'ddarp' | 'wireguard' | 'bird';
export type ServiceState = 'active' | 'inactive' | 'failed' | 'unknown';

interface ServiceStatusProps extends HTMLAttributes<HTMLDivElement> {
  service: ServiceType;
  state: ServiceState;
  showLabel?: boolean;
  size?: LiveIndicatorSize;
}

const serviceLabels: Record<ServiceType, string> = {
  ddarp: 'DDARP',
  wireguard: 'WireGuard',
  bird: 'BIRD',
};

const stateToVariant: Record<ServiceState, LiveIndicatorVariant> = {
  active: 'success',
  inactive: 'warning',
  failed: 'danger',
  unknown: 'primary',
};

const stateLabels: Record<ServiceState, string> = {
  active: 'Active',
  inactive: 'Inactive',
  failed: 'Failed',
  unknown: 'Unknown',
};

export const ServiceStatus = ({
  service,
  state,
  showLabel = true,
  size = 'sm',
  className,
  ...props
}: ServiceStatusProps) => {
  const variant = stateToVariant[state];
  const { dot } = sizeStyles[size];
  const { dot: dotColor, text: textColor } = variantStyles[variant];

  return (
    <div
      className={cn('inline-flex items-center gap-2', className)}
      title={`${serviceLabels[service]}: ${stateLabels[state]}`}
      {...props}
    >
      <span className="relative flex">
        {state === 'active' && (
          <motion.span
            className={cn('absolute inline-flex rounded-full opacity-75', dot, dotColor)}
            animate={{ scale: [1, 1.4, 1], opacity: [0.75, 0, 0.75] }}
            transition={{ repeat: Infinity, duration: 2 }}
          />
        )}
        <span className={cn('relative inline-flex rounded-full', dot, dotColor)} />
      </span>
      {showLabel && (
        <span className={cn('text-xs font-medium', textColor)}>
          {serviceLabels[service]}
        </span>
      )}
    </div>
  );
};

// Combined Service Status - shows all three services
interface CombinedServiceStatusProps extends HTMLAttributes<HTMLDivElement> {
  ddarp: ServiceState;
  wireguard: ServiceState;
  bird: ServiceState;
  compact?: boolean;
}

export const CombinedServiceStatus = ({
  ddarp,
  wireguard,
  bird,
  compact = false,
  className,
  ...props
}: CombinedServiceStatusProps) => {
  if (compact) {
    return (
      <div className={cn('inline-flex items-center gap-1', className)} {...props}>
        <ServiceStatus service="ddarp" state={ddarp} showLabel={false} size="sm" />
        <ServiceStatus service="wireguard" state={wireguard} showLabel={false} size="sm" />
        <ServiceStatus service="bird" state={bird} showLabel={false} size="sm" />
      </div>
    );
  }

  return (
    <div className={cn('flex items-center gap-4', className)} {...props}>
      <ServiceStatus service="ddarp" state={ddarp} />
      <ServiceStatus service="wireguard" state={wireguard} />
      <ServiceStatus service="bird" state={bird} />
    </div>
  );
};

export default LiveIndicator;
